import { createBrowserRouter } from "react-router-dom";
import { lazy, ComponentType } from "react";
import { Navigate } from "react-router-dom";
import ProtectedRoute from "../auth/ProtectedRoute";
// Not lazy — a tiny wrapper that keeps the "Ask about this candidate" chat
// widget mounted across profile tab switches (see ProfileLayout).
import ProfileLayout from "../Talent/components/profile/ProfileLayout";

function lazyRetry(factory: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((err) => {
      const isChunkError =
        err?.name === "ChunkLoadError" ||
        err?.message?.includes("Loading chunk") ||
        err?.message?.includes("Failed to fetch dynamically imported module");

      if (isChunkError) {
        const reloaded = sessionStorage.getItem("chunk_reload");
        if (!reloaded) {
          sessionStorage.setItem("chunk_reload", "1");
          const url = new URL(window.location.href);
          url.searchParams.set("_cb", Date.now().toString());
          window.location.replace(url.toString());
          return new Promise(() => {});
        }
        sessionStorage.removeItem("chunk_reload");
      }
      throw err;
    }),
  );
}

// Auth pages
const LoginPage = lazyRetry(() => import("../auth/LoginPage"));

// Lazy load app modules for code splitting
const TalentLayout = lazyRetry(() => import("../Talent/TalentLayout"));
const PlatformLayout = lazyRetry(() => import("../Platform/PlatformLayout"));
const CampaignLayout = lazyRetry(() => import("../Campaign/CampaignLayout"));
const AgentWorkflowLayout = lazyRetry(() => import("../AgentWorkflow/AgentWorkflowLayout"));
const AgentInboxPage = lazyRetry(() => import("../AgentWorkflow/pages/AgentInboxPage"));
const AgentInboxDashboardPage = lazyRetry(() => import("../AgentWorkflow/pages/InboxDashboardPage"));
const AgentEscalationsPage = lazyRetry(() => import("../AgentWorkflow/pages/AgentEscalationsPage"));
const AgentEscalationInsightsPage = lazyRetry(() => import("../AgentWorkflow/pages/EscalationInsightsPage"));
const AgentListPage = lazyRetry(() => import("../AgentWorkflow/pages/AgentListPage"));
const AgentDetailPage = lazyRetry(() => import("../AgentWorkflow/pages/AgentDetailPage"));
const AgentWorkflowToolsPage = lazyRetry(() => import("../AgentWorkflow/pages/ToolsPage"));
const AgentWorkflowModelsPage = lazyRetry(() => import("../AgentWorkflow/pages/ModelsPage"));
const ContextMapsListPage = lazyRetry(() => import("../AgentWorkflow/pages/ContextMapsListPage"));
const ContextMapDetailPage = lazyRetry(() => import("../AgentWorkflow/pages/ContextMapDetailPage"));
const RoutingRulesPage = lazyRetry(() => import("../AgentWorkflow/pages/RoutingRulesPage"));
const Dashboard = lazyRetry(() => import("../components/shared/Dashboard"));

// Admin Panel
const AdminLayout = lazyRetry(() => import("../Admin/AdminLayout"));
const AppPrioritizationLayout = lazyRetry(() => import("../AppPrioritization/AppPrioritizationLayout"));
const AppPrioritizationPage = lazyRetry(() => import("../AppPrioritization/pages/PrioritizationPage"));
const AppPrioritizationTrackingPage = lazyRetry(() => import("../AppPrioritization/pages/TrackingPage"));
const AppPrioritizationCurrentTrackingPage = lazyRetry(() => import("../AppPrioritization/pages/CurrentTrackingPage"));

// Engineering tracker — email allowlist (canSeeEngineering), gated in its layout
const EngineeringLayout = lazyRetry(() => import("../Engineering/EngineeringLayout"));
const EngineeringBoardPage = lazyRetry(() => import("../Engineering/pages/BoardPage"));
const EngineeringCommitsPage = lazyRetry(() => import("../Engineering/pages/CommitsPage"));
const EngineeringReposPage = lazyRetry(() => import("../Engineering/pages/ReposPage"));
const EngineeringVelocityPage = lazyRetry(() => import("../Engineering/pages/VelocityPage"));
const EngineeringFeaturesPage = lazyRetry(() => import("../Engineering/pages/FeaturesPage"));
const EngineeringFlowPage = lazyRetry(() => import("../Engineering/pages/FlowPage"));

// Fast Track Centralization
const FastTrackLayout = lazyRetry(() => import("../FastTrack/FastTrackLayout"));
const FastTrackAppPage = lazyRetry(() => import("../FastTrack/pages/FastTrackApp"));
const FastTrackAgentsPage = lazyRetry(() => import("../FastTrack/pages/AgentsPage"));
const FastTrackConversionPage = lazyRetry(() => import("../FastTrack/pages/ConversionPage"));
const ToFuLayout = lazyRetry(() => import("../ToFu/ToFuLayout"));
const ToFuAppPage = lazyRetry(() => import("../ToFu/pages/ToFuApp"));
const AdminRolesPage = lazyRetry(() => import("../Admin/pages/RolesPage"));
const AdminInterviewerOffboardingPage = lazyRetry(() => import("../Admin/pages/InterviewerOffboardingPage"));
const AdminInterviewNotesExportPage = lazyRetry(() => import("../Admin/pages/InterviewNotesExportPage"));
const AdminUsersPage = lazyRetry(() => import("../Admin/pages/UsersPage"));
const AdminSessionsPage = lazyRetry(() => import("../Admin/pages/SessionsPage"));
const MfaPage = lazyRetry(() => import("../auth/MfaPage"));
const AdminUserOffboardingPage = lazyRetry(() => import("../Admin/pages/UserAccessOffboardingPage"));
const AdminMaintenancePage = lazyRetry(() => import("../Admin/pages/MaintenanceAdminPage"));
const AdminInterviewTemplatesPage = lazyRetry(() => import("../Admin/pages/InterviewTemplatesPage"));
const AdminIndustriesPage = lazyRetry(() => import("../Admin/pages/IndustriesPage"));
const AdminSkillsPage = lazyRetry(() => import("../Admin/pages/SkillsPage"));
const AdminSkillDetailPage = lazyRetry(() => import("../Admin/pages/SkillDetailPage"));
const AdminToolsPage = lazyRetry(() => import("../Admin/pages/ToolsPage"));
const AdminAccountManagerConfigPage = lazyRetry(
  () => import("../Admin/pages/AccountManagerConfigPage"),
);
const AdminAeSpecialistsPage = lazyRetry(
  () => import("../Admin/pages/AeSpecialistsPage"),
);
const AdminPlatformSpecialistsPage = lazyRetry(
  () => import("../Admin/pages/PlatformSpecialistsPage"),
);
const AdminDuplicatesPage = lazyRetry(() => import("../Admin/pages/DuplicateProfilesPage"));
const AdminAttributeConfidencePage = lazyRetry(
  () => import("../Admin/pages/AttributeConfidencePage"),
);

// Talent pages
// Recruiter Dashboard ("My Dashboard") is the /talent index — it REPLACES the old
// TalentDashboardPage (removed from the frontend; the file remains in git history).
const RecruiterDashboardPage = lazyRetry(
  () => import("../Talent/pages/RecruiterDashboardPage"),
);
const ActivePage = lazyRetry(() => import("../Talent/pages/ActivePage"));
const MatchedPage = lazyRetry(() => import("../Talent/pages/MatchedPage"));
const AllPage = lazyRetry(() => import("../Talent/pages/AllPage"));
const ApplicantsPage = lazyRetry(
  () => import("../Talent/pages/ApplicantsPage"),
);
const AutoRoutingApplicantsPage = lazyRetry(
  () => import("../Talent/pages/AutoRoutingApplicantsPage"),
);
const ManualReviewApplicantsPage = lazyRetry(
  () => import("../Talent/pages/ManualReviewApplicantsPage"),
);
const PriorityReviewApplicantsPage = lazyRetry(
  () => import("../Talent/pages/PriorityReviewApplicantsPage"),
);
const RilletManualReviewApplicantsPage = lazyRetry(
  () => import("../Talent/pages/RilletManualReviewApplicantsPage"),
);
const InterviewsPage = lazyRetry(
  () => import("../Talent/pages/InterviewsPage"),
);
const InterviewsTechnicalPage = lazyRetry(
  () => import("../Talent/pages/InterviewsTechnicalPage"),
);
const InterviewsBehavioralPage = lazyRetry(
  () => import("../Talent/pages/InterviewsBehavioralPage"),
);
const FastTrackPage = lazyRetry(() => import("../Talent/pages/FastTrackPage"));
const CooldownPage = lazyRetry(() => import("../Talent/pages/CooldownPage"));
const PrioritizationRejectedPage = lazyRetry(() => import("../Talent/pages/PrioritizationRejectedPage"));
const InactivePage = lazyRetry(() => import("../Talent/pages/InactivePage"));
const OnboardingPage = lazyRetry(
  () => import("../Talent/pages/OnboardingPage"),
);
const PassivePage = lazyRetry(() => import("../Talent/pages/PassivePage"));
const BacklogPage = lazyRetry(() => import("../Talent/pages/BacklogPage"));
const ReferralsPage = lazyRetry(() => import("../Talent/pages/ReferralsPage"));
const ScheduledCallsPage = lazyRetry(
  () => import("../Talent/pages/ScheduledCallsPage"),
);
const WebhookLogsPage = lazyRetry(
  () => import("../Talent/pages/WebhookLogsPage"),
);
const ReportsHubPage = lazyRetry(
  () => import("../Talent/pages/ReportsHubPage"),
);
const FirstRoundPerformancePage = lazyRetry(
  () => import("../Talent/pages/reports/FirstRoundPerformancePage"),
);
const FinalRoundPerformancePage = lazyRetry(
  () => import("../Talent/pages/reports/FinalRoundPerformancePage"),
);
const RecruiterSummaryPage = lazyRetry(
  () => import("../Talent/pages/reports/RecruiterSummaryPage"),
);
const AiInterviewFunnelPage = lazyRetry(
  () => import("../Talent/pages/reports/AiInterviewFunnelPage"),
);
const SourceFunnelPage = lazyRetry(
  () => import("../Talent/pages/reports/SourceFunnelPage"),
);
const OverallFunnelSankeyPage = lazyRetry(
  () => import("../Talent/pages/reports/OverallFunnelSankeyPage"),
);
const FunnelDashboardPage = lazyRetry(
  () => import("../Talent/pages/reports/FunnelDashboardPage"),
);
const FunnelHealthPage = lazyRetry(
  () => import("../Talent/pages/reports/FunnelHealthPage"),
);
const NetworkCompositionPage = lazyRetry(
  () => import("../Talent/pages/reports/NetworkCompositionPage"),
);
const InterviewCompletionPage = lazyRetry(
  () => import("../Talent/pages/reports/InterviewCompletionPage"),
);
const BehavioralCompletionPage = lazyRetry(
  () => import("../Talent/pages/reports/BehavioralCompletionPage"),
);
const AiInterviewResultsPage = lazyRetry(
  () => import("../Talent/pages/reports/AiInterviewResultsPage"),
);
const AutoCancelledInterviewsPage = lazyRetry(
  () => import("../Talent/pages/reports/AutoCancelledInterviewsPage"),
);
const TalentDetectivePage = lazyRetry(
  () => import("../Talent/pages/TalentDetectivePage"),
);
// three.js lives only in this chunk, so the rest of the app doesn't grow.
const PipelineUniversePage = lazyRetry(
  () => import("../Talent/pipelineUniverse/PipelineUniversePage"),
);
const VettingFunnelPage = lazyRetry(
  () => import("../Talent/pages/reports/VettingFunnelPage"),
);
const LeadScoreRejectionsPage = lazyRetry(
  () => import("../Talent/pages/reports/LeadScoreRejectionsPage"),
);

// Profile pages (using the detailed profile component with sidebar)
const ProfilePage = lazyRetry(
  () => import("../Talent/components/profile/ProfilePage"),
);
const ProfileInterviewsPage = lazyRetry(
  () => import("../Talent/components/profile/InterviewsPage"),
);
const ProfileTimelinePage = lazyRetry(
  () => import("../Talent/components/profile/TimelinePage"),
);
const ProfileOnboardingPage = lazyRetry(
  () => import("../Talent/components/profile/OnboardingPage"),
);
const ProfileExternalLinksPage = lazyRetry(
  () => import("../Talent/components/profile/ExternalLinksPage"),
);
const ProfileOpportunitiesPage = lazyRetry(
  () => import("../Talent/components/profile/ProfileOpportunitiesPage"),
);
const PlatformProfilePage = lazyRetry(
  () => import("../Talent/components/profile/PlatformProfilePage"),
);

// Platform pages
const UnifiedPlatformDashboardPage = lazyRetry(
  () => import("../Platform/pages/UnifiedPlatformDashboardPage"),
);
const CompanyManagerPage = lazyRetry(
  () => import("../Platform/pages/CompanyManagerPage"),
);
const CompanyProfilePage = lazyRetry(
  () => import("../Platform/pages/CompanyProfilePage"),
);
const PositionTrackerPage = lazyRetry(
  () => import("../Platform/pages/PositionTrackerPage"),
);
const PositionDetailPage = lazyRetry(
  () => import("../Platform/pages/PositionDetailPage"),
);
const JobOpeningMatchPage = lazyRetry(
  () => import("../Platform/pages/JobOpeningMatchPage"),
);
const CandidatesInProcessPage = lazyRetry(
  () => import("../Platform/pages/CandidatesInProcessPage"),
);
const ActivityPage = lazyRetry(() => import("../Platform/pages/ActivityPage"));
const OpportunitiesTrackerPage = lazyRetry(
  () => import("../Platform/pages/OpportunitiesTrackerPage"),
);
const SalesLayout = lazyRetry(() => import("../Sales/SalesLayout"));
const NewOpportunityPage = lazyRetry(
  () => import("../Platform/pages/NewOpportunityPage"),
);
const NewOpportunityCallConsolePage = lazyRetry(
  () => import("../Platform/pages/NewOpportunityCallConsolePage"),
);
const OpportunityCreationPage = lazyRetry(
  () => import("../Platform/pages/OpportunityCreationPage"),
);
const SalesPrepPage = lazyRetry(() => import("../Sales/pages/SalesPrepPage"));
const SalesOpportunitiesPage = lazyRetry(
  () => import("../Sales/pages/SalesOpportunitiesPage"),
);
const SalesDashboardPage = lazyRetry(
  () => import("../Sales/pages/SalesDashboardPage"),
);
const ArchivedOpportunitiesPage = lazyRetry(
  () => import("../Platform/pages/ArchivedOpportunitiesPage"),
);
const MonthlyOpportunitiesPage = lazyRetry(
  () => import("../Platform/pages/MonthlyOpportunitiesPage"),
);
const MatchIntelligencePage = lazyRetry(
  () => import("../Platform/pages/MatchIntelligencePage"),
);
const UniverseMapPage = lazyRetry(
  () => import("../Platform/pages/UniverseMapPage"),
);
const SupplyDemandPage = lazyRetry(
  () => import("../Platform/pages/SupplyDemandPage"),
);
const OpportunityPrioritizationPage = lazyRetry(
  () => import("../Platform/pages/OpportunityPrioritizationPage"),
);
const MyEmailSignaturePage = lazyRetry(
  () => import("../Platform/pages/MyEmailSignaturePage"),
);
const TalentEmailsPage = lazyRetry(() => import("../Platform/pages/TalentEmailsPage"));
// sigma (WebGL) + graphology live only in this chunk, so the rest of the app doesn't grow.
const SemanticGraphPage = lazyRetry(
  () => import("../Platform/semanticGraph/SemanticGraphPage"),
);

// Customer Success pages
const CsLayout = lazyRetry(() => import("../CustomerSuccess/CsLayout"));
const CsDashboardPage = lazyRetry(
  () => import("../CustomerSuccess/pages/DashboardPage"),
);
const CsCompanyListPage = lazyRetry(
  () => import("../CustomerSuccess/pages/CompanyListPage"),
);
const CsCompanyDetailPage = lazyRetry(
  () => import("../CustomerSuccess/pages/CompanyDetailPage"),
);
const CsContractListPage = lazyRetry(
  () => import("../CustomerSuccess/pages/ContractListPage"),
);
const CsContractDetailPage = lazyRetry(
  () => import("../CustomerSuccess/pages/ContractDetailPage"),
);
const CsInvoicingPage = lazyRetry(
  () => import("../CustomerSuccess/pages/InvoicingPage"),
);
// Designed-but-unbuilt CS surfaces. One module, three named exports.
const CsOngoingReviewPage = lazyRetry(
  () => import("../CustomerSuccess/pages/OngoingReviewPage"),
);
const CsContractEventsPage = lazyRetry(
  () => import("../CustomerSuccess/pages/ContractEventsPage"),
);
const CsIntegrationsPage = lazyRetry(() =>
  import("../CustomerSuccess/pages/ComingSoonPage").then((m) => ({
    default: m.IntegrationsPage,
  })),
);
const CsWorksheetImportPage = lazyRetry(() =>
  import("../CustomerSuccess/pages/ComingSoonPage").then((m) => ({
    default: m.WorksheetImportPage,
  })),
);
const CsTalentListPage = lazyRetry(
  () => import("../CustomerSuccess/pages/TalentListPage"),
);
const CsTalentHotlinePage = lazyRetry(
  () => import("../CustomerSuccess/pages/TalentHotlinePage"),
);
const CsTalentDetailPage = lazyRetry(
  () => import("../CustomerSuccess/pages/TalentDetailPage"),
);
const CsIssueListPage = lazyRetry(
  () => import("../CustomerSuccess/pages/IssueListPage"),
);
const CsIssueDetailPage = lazyRetry(
  () => import("../CustomerSuccess/pages/IssueDetailPage"),
);
const CsIssueFormPage = lazyRetry(
  () => import("../CustomerSuccess/pages/IssueFormPage"),
);
const CsNextActionsPage = lazyRetry(
  () => import("../CustomerSuccess/pages/NextActionsPage"),
);
const CsReportsPage = lazyRetry(
  () => import("../CustomerSuccess/pages/ReportsPage"),
);
const CsEmailCheckInsPage = lazyRetry(
  () => import("../CustomerSuccess/pages/EmailCheckInsPage"),
);
const CsTemplateEditorPage = lazyRetry(
  () => import("../CustomerSuccess/pages/TemplateEditorPage"),
);
const CsSequenceBuilderPage = lazyRetry(
  () => import("../CustomerSuccess/pages/SequenceBuilderPage"),
);
const CsUpsellListPage = lazyRetry(
  () => import("../CustomerSuccess/pages/UpsellListPage"),
);
const CsUpsellDetailPage = lazyRetry(
  () => import("../CustomerSuccess/pages/UpsellDetailPage"),
);
const CsOngoingReplacementsPage = lazyRetry(
  () => import("../CustomerSuccess/pages/OngoingReplacementsPage"),
);
const CsSettingsPage = lazyRetry(
  () => import("../CustomerSuccess/pages/CsSettingsPage"),
);

// Campaign pages
const CampaignDashboardPage = lazyRetry(
  () => import("../Campaign/pages/CampaignDashboardPage"),
);
const MonitoringPage = lazyRetry(
  () => import("../Campaign/pages/MonitoringPage"),
);
const MofuPage = lazyRetry(() => import("../Campaign/pages/MofuPage"));
const BofuPage = lazyRetry(() => import("../Campaign/pages/BofuPage"));
const AbTestsPage = lazyRetry(() => import("../Campaign/pages/AbTestsPage"));
const FlowsPage = lazyRetry(() => import("../Campaign/pages/FlowsPage"));
const EmailQueuePriorityPage = lazyRetry(() => import("../Campaign/pages/EmailQueuePriorityPage"));
const SenderMetricsPage = lazyRetry(
  () => import("../Campaign/pages/SenderMetricsPage"),
);
const WhatsAppPage = lazyRetry(() => import("../Campaign/pages/WhatsAppPage"));
const RemindersPage = lazyRetry(() => import("../Campaign/pages/RemindersPage"));

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    // Email OTP after Cognito sign-in (2026-09-15) — authenticated but outside
    // ProtectedRoute, which is what redirects here while the code is pending.
    path: "/mfa",
    element: <MfaPage />,
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin",
    element: <ProtectedRoute><AdminLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <Navigate to="roles" replace /> },
      { path: "roles", element: <AdminRolesPage /> },
      { path: "interviewer-offboarding", element: <AdminInterviewerOffboardingPage /> },
      { path: "interview-notes-export", element: <AdminInterviewNotesExportPage /> },
      { path: "users", element: <AdminUsersPage /> },
      { path: "sessions", element: <AdminSessionsPage /> },
      { path: "user-offboarding", element: <AdminUserOffboardingPage /> },
      { path: "maintenance", element: <AdminMaintenancePage /> },
      { path: "interview-templates", element: <AdminInterviewTemplatesPage /> },
      { path: "industries", element: <AdminIndustriesPage /> },
      { path: "skills", element: <AdminSkillsPage /> },
      { path: "skills/:id", element: <AdminSkillDetailPage /> },
      { path: "tools", element: <AdminToolsPage /> },
      { path: "account-manager-config", element: <AdminAccountManagerConfigPage /> },
      { path: "platform-specialists", element: <AdminPlatformSpecialistsPage /> },
      { path: "ae-specialists", element: <AdminAeSpecialistsPage /> },
      { path: "attribute-confidence", element: <AdminAttributeConfidencePage /> },
      { path: "duplicates", element: <AdminDuplicatesPage /> },
    ],
  },
  {
    path: "/app-prioritization",
    element: <ProtectedRoute><AppPrioritizationLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <Navigate to="first-round" replace /> },
      // Static `tracking` route matches before the `:tab` catch-all (analytics tab, not
      // the config editor — separate component so it doesn't carry the editor's state).
      { path: "tracking", element: <AppPrioritizationTrackingPage /> },
      { path: "current-tracking", element: <AppPrioritizationCurrentTrackingPage /> },
      // Single `:tab` element so switching First Round ⇄ Final Round keeps the same
      // component instance (unsaved config edits survive the tab change).
      { path: ":tab", element: <AppPrioritizationPage /> },
    ],
  },
  {
    path: "/engineering",
    element: <ProtectedRoute><EngineeringLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <EngineeringBoardPage /> },
      // A shareable link to one item: the board with that item open (/engineering/items/ENG-287).
      { path: "items/:key", element: <EngineeringBoardPage /> },
      { path: "commits", element: <EngineeringCommitsPage /> },
      { path: "repos", element: <EngineeringReposPage /> },
      { path: "velocity", element: <EngineeringVelocityPage /> },
      { path: "features", element: <EngineeringFeaturesPage /> },
      { path: "flow", element: <EngineeringFlowPage /> },
    ],
  },
  {
    path: "/fast-track",
    element: <ProtectedRoute><FastTrackLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <FastTrackAppPage /> },
      { path: "agents", element: <FastTrackAgentsPage /> },
      { path: "conversion", element: <FastTrackConversionPage /> },
    ],
  },
  {
    path: "/tofu",
    element: <ProtectedRoute><ToFuLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <ToFuAppPage /> },
    ],
  },
  {
    // Sales app — the New Opportunity flow (chooser → live call console →
    // opportunity creation card). Entered from the home launcher's Sales card.
    path: "/sales",
    element: <ProtectedRoute><SalesLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <NewOpportunityPage /> },
      { path: "opportunities", element: <SalesOpportunitiesPage /> },
      { path: "dashboard", element: <SalesDashboardPage /> },
      { path: "prep", element: <SalesPrepPage /> },
      { path: "console", element: <NewOpportunityCallConsolePage /> },
      { path: "card", element: <OpportunityCreationPage /> },
    ],
  },
  {
    path: "/talent",
    element: (
      <ProtectedRoute>
        <TalentLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <RecruiterDashboardPage /> },
      { path: "recruiter-dashboard", element: <RecruiterDashboardPage /> },
      { path: "active", element: <ActivePage /> },
      { path: "matched", element: <MatchedPage /> },
      { path: "all", element: <AllPage /> },
      { path: "applicants", element: <ApplicantsPage /> },
      {
        path: "applicants/auto-routing",
        element: <AutoRoutingApplicantsPage />,
      },
      {
        path: "applicants/manual-review",
        element: <ManualReviewApplicantsPage />,
      },
      {
        path: "applicants/priority-review",
        element: <PriorityReviewApplicantsPage />,
      },
      {
        path: "applicants/rillet-manual-review",
        element: <RilletManualReviewApplicantsPage />,
      },
      { path: "interviews", element: <InterviewsPage /> },
      { path: "interviews/technical", element: <InterviewsTechnicalPage /> },
      { path: "interviews/behavioral", element: <InterviewsBehavioralPage /> },
      { path: "fast-track", element: <FastTrackPage /> },
      // Talent Detective (2026-10-05): urgent sourcing over people still in
      // interviews, no opportunity. Opened from the top bar, next to Talent AI.
      { path: "detective", element: <TalentDetectivePage /> },
      // Pipeline Universe (2026-10-06): every candidate as a star at their current
      // stage, in 3D. Full screen over the layout; opened next to the Detective.
      { path: "pipeline-universe", element: <PipelineUniversePage /> },
      { path: "onboarding", element: <OnboardingPage /> },
      // Same M5 universe map as /platform/universe-map, mounted under the
      // Talent layout so the Talent sidebar's MAVI Network entry stays in-app.
      { path: "universe-map", element: <UniverseMapPage /> },
      { path: "rejected", element: <CooldownPage /> },
      { path: "rejected/prioritization", element: <PrioritizationRejectedPage /> },
      { path: "inactive", element: <InactivePage /> },
      { path: "passive", element: <PassivePage /> },
      { path: "backlog", element: <BacklogPage /> },
      { path: "referrals", element: <ReferralsPage /> },
      { path: "scheduled-calls", element: <ScheduledCallsPage /> },
      { path: "webhook-logs", element: <WebhookLogsPage /> },
      { path: "system-logs", element: <WebhookLogsPage /> },
      { path: "reports", element: <ReportsHubPage /> },
      {
        path: "reports/first-round-performance",
        element: <FirstRoundPerformancePage />,
      },
      {
        path: "reports/final-round-performance",
        element: <FinalRoundPerformancePage />,
      },
      { path: "reports/recruiter-summary", element: <RecruiterSummaryPage /> },
      {
        path: "reports/ai-interview-funnel",
        element: <AiInterviewFunnelPage />,
      },
      { path: "reports/source-geo-funnel", element: <SourceFunnelPage /> },
      { path: "reports/vetting-funnel", element: <VettingFunnelPage /> },
      { path: "reports/lead-score-rejections", element: <LeadScoreRejectionsPage /> },
      { path: "reports/overall-funnel-sankey", element: <OverallFunnelSankeyPage /> },
      { path: "reports/funnel-dashboard", element: <FunnelDashboardPage /> },
      { path: "reports/funnel-health", element: <FunnelHealthPage /> },
      {
        path: "reports/network-composition",
        element: <NetworkCompositionPage />,
      },
      {
        path: "reports/interview-completion",
        element: <InterviewCompletionPage />,
      },
      {
        path: "reports/behavioral-completion",
        element: <BehavioralCompletionPage />,
      },
      {
        path: "reports/ai-interview-results",
        element: <AiInterviewResultsPage />,
      },
      {
        path: "reports/auto-cancelled-interviews",
        element: <AutoCancelledInterviewsPage />,
      },
      {
        // Shared layout so the "Ask about this candidate" chat widget stays
        // mounted while the tab (<Outlet/>) changes. Child paths keep their
        // exact full URLs (profile/:id, profile/:id/interviews, …).
        path: "profile/:id",
        element: <ProfileLayout />,
        children: [
          { index: true, element: <ProfilePage /> },
          { path: "platform-profile", element: <PlatformProfilePage /> },
          { path: "interviews", element: <ProfileInterviewsPage /> },
          { path: "timeline", element: <ProfileTimelinePage /> },
          { path: "onboarding", element: <ProfileOnboardingPage /> },
          { path: "opportunities", element: <ProfileOpportunitiesPage /> },
          { path: "external-links", element: <ProfileExternalLinksPage /> },
        ],
      },
    ],
  },
  {
    path: "/platform",
    element: (
      <ProtectedRoute>
        <PlatformLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <UnifiedPlatformDashboardPage /> },
      { path: "dashboard/executive", element: <Navigate to="/platform" replace /> },
      { path: "dashboard/specialist", element: <Navigate to="/platform" replace /> },
      { path: "company-manager", element: <CompanyManagerPage /> },
      { path: "company-profile/:id", element: <CompanyProfilePage /> },
      { path: "positions", element: <PositionTrackerPage /> },
      { path: "positions/:id/match", element: <JobOpeningMatchPage /> },
      { path: "positions/:id", element: <PositionDetailPage /> },
      { path: "candidates-in-process", element: <CandidatesInProcessPage /> },
      { path: "opportunities", element: <OpportunitiesTrackerPage /> },
      { path: "prioritization", element: <OpportunityPrioritizationPage /> },
      { path: "opportunities/archive", element: <ArchivedOpportunitiesPage /> },
      { path: "opportunities-monthly", element: <MonthlyOpportunitiesPage /> },
      { path: "activity", element: <ActivityPage /> },
      { path: "match-intelligence", element: <MatchIntelligencePage /> },
      { path: "supply-demand", element: <SupplyDemandPage /> },
      { path: "universe-map", element: <UniverseMapPage /> },
      { path: "email-signature", element: <MyEmailSignaturePage /> },
      { path: "talent-emails", element: <TalentEmailsPage /> },
      { path: "semantic-graph", element: <SemanticGraphPage /> },
    ],
  },
  {
    path: "/campaigns",
    element: (
      <ProtectedRoute>
        <CampaignLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <CampaignDashboardPage /> },
      { path: "monitoring", element: <MonitoringPage /> },
      { path: "ab-tests", element: <AbTestsPage /> },
      { path: "flows", element: <FlowsPage /> },
      { path: "mofu", element: <MofuPage /> },
      { path: "bofu", element: <BofuPage /> },
      { path: "whatsapp", element: <WhatsAppPage /> },
      { path: "reminders", element: <RemindersPage /> },
      { path: "priority", element: <EmailQueuePriorityPage /> },
      { path: "sender-metrics", element: <SenderMetricsPage /> },
    ],
  },
  {
    path: "/cs",
    element: (
      <ProtectedRoute>
        <CsLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <CsDashboardPage /> },
      { path: "companies", element: <CsCompanyListPage /> },
      { path: "companies/:id", element: <CsCompanyDetailPage /> },
      { path: "contracts", element: <CsContractListPage /> },
      { path: "contracts/:id", element: <CsContractDetailPage /> },
      { path: "ongoing-review", element: <CsOngoingReviewPage /> },
      { path: "contract-events", element: <CsContractEventsPage /> },
      { path: "invoicing", element: <CsInvoicingPage /> },
      { path: "integrations", element: <CsIntegrationsPage /> },
      { path: "worksheet-import", element: <CsWorksheetImportPage /> },
      { path: "talent", element: <CsTalentListPage /> },
      { path: "talent/:id", element: <CsTalentDetailPage /> },
      { path: "hotline", element: <CsTalentHotlinePage /> },
      { path: "replacements", element: <CsOngoingReplacementsPage /> },
      { path: "next-actions", element: <CsNextActionsPage /> },
      { path: "issues", element: <CsIssueListPage /> },
      { path: "issues/new", element: <CsIssueFormPage /> },
      { path: "issues/:id", element: <CsIssueDetailPage /> },
      { path: "reports", element: <CsReportsPage /> },
      { path: "email-check-ins", element: <CsEmailCheckInsPage /> },
      {
        path: "email-check-ins/sequences/:id",
        element: <CsSequenceBuilderPage />,
      },
      { path: "email-check-ins/:id", element: <CsTemplateEditorPage /> },
      { path: "upsells", element: <CsUpsellListPage /> },
      // Upsell next actions live on the shared Next Actions page (Upsells tab).
      // Declared before `upsells/:id` so the literal segment wins over the param.
      {
        path: "upsells/next-actions",
        element: <Navigate to="/cs/next-actions?tab=upsells" replace />,
      },
      { path: "upsells/:id", element: <CsUpsellDetailPage /> },
      { path: "settings", element: <CsSettingsPage /> },
    ],
  },
  {
    path: "/agent-workflow",
    element: (
      <ProtectedRoute>
        <AgentWorkflowLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <AgentInboxPage /> },
      { path: "inbox", element: <AgentInboxPage /> },
      { path: "dashboard", element: <AgentInboxDashboardPage /> },
      { path: "escalations", element: <AgentEscalationInsightsPage /> },
      { path: "needs-attention", element: <AgentEscalationsPage /> },
      { path: "agents", element: <AgentListPage /> },
      { path: "agents/:id", element: <AgentDetailPage /> },
      { path: "context-maps", element: <ContextMapsListPage /> },
      { path: "context-maps/:id", element: <ContextMapDetailPage /> },
      { path: "routing-rules", element: <RoutingRulesPage /> },
      { path: "tools", element: <AgentWorkflowToolsPage /> },
      { path: "models", element: <AgentWorkflowModelsPage /> },
    ],
  },
]);
